import React from "react";
import { map, startCase, upperCase } from "lodash";
import { useDropzone as useReactDropzone } from "react-dropzone";
import { FormProvider, useFieldArray, useForm } from "react-hook-form";
import { MIME_TYPE, STATES } from "common/constants";
import { buildFullLink, getPath } from "common/routing";
import { getClientAppPrefix } from "common/utils/url";
import {
  CoastalADetermination,
  Datum,
  useCreateAccountMutation,
} from "../../../generated/graphql";
import { FIRMType } from "../../../generated/graphql";
import { useMultipartFileUpload } from "../../../hooks/useMultipartFileUpload";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import {
  Header,
  Section,
  SectionDivider,
} from "../../Common/__styles__/FullPageFormLayout";
import { Button } from "../../Common/Buttons/Button";
import FullPageFormLayout from "../../Common/FullPageFormLayout";
import { Title } from "../../Common/Typography";
import {
  Number,
  Select,
  SingleFileUpload,
  Text,
} from "../../Inputs/react-hook-form";
import { Error, Remove, Row } from "./__styles__/CreateAccount";

export type AccountCreationForm = {
  name: string;
  state: string;
  fiveDigitPanelPrefix: string;
  datumConversionOffset: Maybe<number>;
  communityNames: Array<{ name: string }>;
  communityNumber: string;
  coastalADeterminationMethod: CoastalADetermination;
  subdomain: string;
  boundaries: { blob: any };
  publicPortal: { color: string };
  firms: Array<{
    isDefault: boolean;
    type: FIRMType;
    datum: Datum;
  }>;
};

interface Props {
  useDropzone?: (
    args: Pick<NonNullable<Parameters<typeof useReactDropzone>[0]>, "onDrop">,
  ) => ReturnType<typeof useReactDropzone>;
  wait?: typeof setTimeout;
}

export const CreateAccount = ({ useDropzone, wait = setTimeout }: Props) => {
  const formMethods = useForm<AccountCreationForm>();

  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const [uploadBoundaryFile, { loading: uploadingBoundary }] =
    useMultipartFileUpload({
      onError: () =>
        addErrorToast("Failed to upload boundary file. Please try again."),
    });
  const [createAccount, { loading: creatingAccount }] =
    useCreateAccountMutation({
      onCompleted: data => {
        addSuccessToast(
          "Account created successfully! Please wait while you get redirected to the account's setting page.",
        );
        // this is a hack to make sure that, when the
        // user is redirected to the new account, the
        // account and all its data have been created
        // we have to wait for the new domain to be live
        // The Route53 docs say this could take up to 1 min
        wait(() => {
          const url = buildFullLink("settings", {
            params: {},
            prefix: getClientAppPrefix(data.createAccount.subdomain),
          });

          window.location.href = url;
        }, 60000);
      },
      onError: () => {
        addErrorToast("Failed to create account! Please contact engineering.");
      },
    });

  const {
    handleSubmit,
    register,
    formState: { errors },
    control,
  } = formMethods;

  const {
    fields: firms,
    append: appendFIRM,
    remove: removeFIRM,
  } = useFieldArray({
    control,
    name: "firms",
    rules: {
      validate: value => {
        if (
          value.map(firm => firm.type).length !==
          new Set(value.map(firm => firm.type)).size
        ) {
          return "You cannot have duplicate FIRM types";
        }

        return true;
      },
    },
  });

  const {
    fields: communityNames,
    append: appendCommunityName,
    remove: removeCommunityName,
  } = useFieldArray({
    control,
    name: "communityNames",
    rules: {
      required: "You must have at least one community name",
    },
  });

  const onSubmit = async (data: AccountCreationForm) => {
    const { boundaries, ...rest } = data;

    if (!boundaries?.blob) {
      addErrorToast(
        "Please upload an account boundary file before submitting.",
      );
      return;
    }

    uploadBoundaryFile({
      file: boundaries.blob,
      onCompleted: async key => {
        await createAccount({
          variables: {
            data: {
              ...rest,
              boundariesKey: key,
              communityNames: map(data.communityNames, "name"),
            },
          },
        });
      },
    });
  };

  const whiteSpaceValidation = {
    value: /^[^\s]+(\s+[^\s]+)*$/,
    message: "Entered value cannot start/end with whitespace",
  };

  const isSubmitting = creatingAccount || uploadingBoundary;

  const rightContainer = (
    <div>
      <Button
        size="small"
        variant="primary"
        onPress={() => handleSubmit(onSubmit)()}
        isPending={isSubmitting}
        title="Create account"
      />
    </div>
  );

  return (
    <FormProvider {...formMethods}>
      <FullPageFormLayout
        subtitle="Create Account"
        prevLocation={getPath("adminAccounts")}
        rightContainer={rightContainer}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Header>
            <Title size="default" type="semiBold">
              Account details
            </Title>
          </Header>
          <Section grid>
            <Text
              label="Name"
              error={errors.name?.message}
              description={"Example: Bay Head, NJ"}
              {...register("name", {
                required: "Name is required",
                pattern: whiteSpaceValidation,
              })}
            />
            <Select
              name="state"
              control={control}
              description={"Example: New Jersey"}
              error={errors.state?.message}
              rules={{ required: "State is required" }}
              options={Object.values(STATES).map(option => {
                return {
                  label: startCase(option.name),
                  value: option.name,
                };
              })}
              label="State"
            />
            <Text
              label="Five Digit Panel Prefix"
              description="Example: 12015"
              error={errors.fiveDigitPanelPrefix?.message}
              {...register("fiveDigitPanelPrefix", {
                pattern: whiteSpaceValidation,
              })}
            />
            <Text
              description="Example: 120062"
              label="Community Number"
              error={errors.communityNumber?.message}
              {...register("communityNumber", {
                required: "Community Number is required",
                pattern: whiteSpaceValidation,
              })}
            />
            <Number
              name="datumConversionOffset"
              label="Datum Conversion Offset"
              register={register}
              error={errors.datumConversionOffset?.message}
            />
            <Select
              name="coastalADeterminationMethod"
              control={control}
              error={errors.coastalADeterminationMethod?.message}
              rules={{ required: "Coastal A determination method is required" }}
              options={Object.values(CoastalADetermination).map(option => {
                return { label: startCase(option), value: option };
              })}
              label="Coastal A determination method"
            />
            <Text
              label="Subdomain"
              description="Example: bayheadnj"
              error={errors.subdomain?.message}
              {...register("subdomain", {
                required: "Subdomain is required",
                pattern: whiteSpaceValidation,
              })}
            />
            <Text
              label="Public Account Color"
              description="Example: #000000"
              error={errors.publicPortal?.color?.message}
              {...register("publicPortal.color", {
                required: "Public Account Color is required",
                pattern: { value: /^#[0-9A-F]{6}$/i, message: "Invalid color" },
              })}
            />
          </Section>
          <SectionDivider />
          <Header>
            <Title size="small" type="semiBold">
              FIRMs
            </Title>
          </Header>
          {errors.firms?.root && <Error>{errors.firms.root.message}</Error>}
          <Section>
            {firms.map((_firm, index) => {
              return (
                <Row key={index}>
                  <Select
                    name={`firms.${index}.type`}
                    control={control}
                    options={Object.values(FIRMType).map(option => {
                      return { label: startCase(option), value: option };
                    })}
                    label="Type"
                  />
                  <Select
                    name={`firms.${index}.datum`}
                    control={control}
                    options={Object.values(Datum).map(option => {
                      return { label: upperCase(option), value: option };
                    })}
                    label="Datum"
                  />
                  <Select
                    name={`firms.${index}.isDefault`}
                    control={control}
                    options={[
                      { label: "Yes", value: true },
                      { label: "No", value: false },
                    ]}
                    label="Default FIRM"
                  />
                  <Remove onClick={() => removeFIRM(index)}>X</Remove>
                </Row>
              );
            })}
            <Button
              variant="primary"
              size="medium"
              onPress={() =>
                appendFIRM({
                  isDefault: false,
                  type: FIRMType.EFFECTIVE,
                  datum: Datum.NAVD_1988,
                })
              }
              title="Add FIRM"
            />
          </Section>
          <SectionDivider />
          <Header>
            <Title size="small" type="semiBold">
              Community names
            </Title>
          </Header>
          {errors.communityNames?.root && (
            <Error>{errors.communityNames.root.message}</Error>
          )}
          <Section>
            {communityNames.map((_community, index) => {
              return (
                <Row key={index}>
                  <Text
                    data-testid={`community-name-${index}`}
                    error={errors.communityNames?.[index]?.name?.message}
                    {...register(`communityNames.${index}.name`, {
                      required: "Name is required",
                      pattern: whiteSpaceValidation,
                    })}
                  />
                  <Remove onClick={() => removeCommunityName(index)}>X</Remove>
                </Row>
              );
            })}
            <Button
              variant="primary"
              size="medium"
              onPress={() => appendCommunityName({ name: "" })}
              title="Add Community Name"
            />
          </Section>
          <SectionDivider />
          <Header>
            <Title size="small" type="semiBold">
              Account boundary
            </Title>
          </Header>
          {errors.boundaries && <Error>{errors.boundaries.message}</Error>}
          <Section>
            <SingleFileUpload
              name="boundaries"
              useDropzone={useDropzone}
              control={control}
              allowedMimeTypes={[MIME_TYPE.GEOJSON]}
              rules={{
                required: "Please upload an account boundary file",
              }}
            />
          </Section>
        </form>
      </FullPageFormLayout>
    </FormProvider>
  );
};
export default CreateAccount;
